import { useEffect, useRef, useState } from 'react';
import {
  ShieldCheck,
  Check,
  MessageSquare,
  Clock,
  ArrowRight,
  ChevronUp,
  ChevronDown,
  Plus,
  Zap,
  ArrowUp,
} from 'lucide-react';
import { useStore } from '../store';
import { Chat } from './Chat';
import { VMark } from '../components/VMark';
import {
  STEPS,
  SKILL_KEYS,
  SKILL_LABELS,
  TIME_LEFT,
  skillBand,
  promptQuality,
  sliderZone,
  gainChips,
  revealText,
  type Step,
  type SkillKey,
} from '../assessment-steps';
import { InfoTip } from '../components/ui';
import { CRITERION_DEF } from '../criteria-copy';

// The same plain-English definitions surfaced on the report, mapped onto the rail's skill
// keys so the live "Your profile" rail explains each criterion as it builds.
const SKILL_DEF: Record<SkillKey, string> = {
  prompt: CRITERION_DEF.prompt_mastery,
  technical: CRITERION_DEF.technical_understanding,
  practical: CRITERION_DEF.practical_application,
  critical: CRITERION_DEF.critical_evaluation,
  workflow: CRITERION_DEF.workflow_design,
};

export function Assessment() {
  const asmtStage = useStore((s) => s.asmtStage);
  const needsAuth = useStore((s) => s.needsAuth);
  const mode = useStore((s) => s.mode);
  // Live builds require sign-in before the assessment (covers the nav-tab entry too).
  if (needsAuth) return <SignInGate />;
  if (asmtStage === 'consent') return <Consent />;
  if (asmtStage === 'intro') return <Intro />;
  if (asmtStage === 'role') return <RolePicker />;
  if (asmtStage === 'setup') return <Setup />;
  // live: the conversational interview, or the quick 6-step walkthrough.
  return mode === 'conversational' ? <Chat /> : <Live />;
}

const arrowIcon = <ArrowRight size={18} strokeWidth={2.2} />;

// Shared primary CTA matching the v3 prototype (green fill, black text, full-width).
function PrimaryButton({
  onClick,
  children,
  disabled = false,
}: {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full"
      style={{
        background: disabled ? '#C8C8CC' : '#00D700',
        color: disabled ? '#75757F' : '#000',
        border: `2px solid ${disabled ? '#C8C8CC' : '#00D700'}`,
        fontSize: 16,
        fontWeight: 700,
        padding: 15,
        borderRadius: 6,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        transition: 'all .12s',
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = '#00BE00';
        e.currentTarget.style.borderColor = '#00BE00';
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = '#00D700';
        e.currentTarget.style.borderColor = '#00D700';
      }}
    >
      {children}
    </button>
  );
}

// Centred light-grey stage backdrop used by consent / intro / setup.
function StageBackdrop({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        flex: 1,
        background: '#F4F4F6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        minHeight: 'calc(100vh - 60px)',
      }}
    >
      {children}
    </div>
  );
}

function SignInGate() {
  const signIn = useStore((s) => s.signIn);
  return (
    <StageBackdrop>
      <div
        className="animate-fadeUp"
        style={{
          maxWidth: 560,
          width: '100%',
          background: '#fff',
          border: '1px solid #E2E2E6',
          borderRadius: 14,
          boxShadow: '0 18px 48px rgba(15,17,26,0.10)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '34px 40px 30px' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 9,
              border: '1px solid #C8E6C8',
              background: '#F6FCF6',
              borderRadius: 999,
              padding: '6px 13px',
              marginBottom: 18,
            }}
          >
            <ShieldCheck size={15} strokeWidth={1.9} color="#00A800" />
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#00A800',
              }}
            >
              Sign in required
            </span>
          </div>
          <h1
            style={{
              fontSize: 28,
              lineHeight: 1.1,
              letterSpacing: '-0.025em',
              fontWeight: 700,
              margin: 0,
              color: '#000',
            }}
          >
            Sign in to begin your assessment
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.55, color: '#75757F', margin: '12px 0 24px' }}>
            Your responses build your personal report, so the assessment runs against your signed-in
            account. You'll be taken to the Versent sign-in and brought straight back.
          </p>
          <PrimaryButton onClick={signIn}>
            Sign in {arrowIcon}
          </PrimaryButton>
        </div>
      </div>
    </StageBackdrop>
  );
}

function Consent() {
  const consentChecked = useStore((s) => s.consentChecked);
  const setConsent = useStore((s) => s.setConsent);
  const setAsmtStage = useStore((s) => s.setAsmtStage);
  const noticeCopy = useStore((s) => s.noticeCopy);

  const checks: { title: string; body: string }[] = [
    {
      title: 'Your responses build your report — for you',
      body: 'Your manager and programme admins see only anonymised, role-level trends. Never your words or your band by name.',
    },
    {
      title: 'Stored securely in Australia, deleted on request',
      body: 'Data is encrypted, hosted in-region, and retained for the campaign period only. You can export or delete yours any time.',
    },
    {
      title: 'Just you and Avery',
      body: 'For an honest read, please answer in your own words — without help from another AI assistant.',
    },
  ];

  return (
    <StageBackdrop>
      <div
        className="animate-fadeUp"
        style={{
          maxWidth: 620,
          width: '100%',
          background: '#fff',
          border: '1px solid #E2E2E6',
          borderRadius: 14,
          boxShadow: '0 18px 48px rgba(15,17,26,0.10)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '34px 40px 30px', borderBottom: '1px solid #EFEFF2' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 9,
              border: '1px solid #C8E6C8',
              background: '#F6FCF6',
              borderRadius: 999,
              padding: '6px 13px',
              marginBottom: 18,
            }}
          >
            <ShieldCheck size={15} strokeWidth={1.9} color="#00A800" />
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#00A800',
              }}
            >
              Before we begin
            </span>
          </div>
          <h1
            style={{
              fontSize: 28,
              lineHeight: 1.1,
              letterSpacing: '-0.025em',
              fontWeight: 700,
              margin: 0,
              color: '#000',
            }}
          >
            How your data is handled
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.55, color: '#75757F', margin: '12px 0 0' }}>
            A 20-second read. You're in control of all of it.
          </p>
        </div>
        <div style={{ padding: '26px 40px 30px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginBottom: 24 }}>
            {checks.map((c) => (
              <div key={c.title} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <Check
                  size={19}
                  strokeWidth={2.2}
                  color="#00A800"
                  style={{ flex: 'none', marginTop: 1 }}
                />
                <div>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: '#000' }}>{c.title}</div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.5, color: '#75757F', marginTop: 2 }}>
                    {c.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <details
            data-testid="collection-notice"
            style={{ background: '#F9F9FB', border: '1px solid #E2E2E6', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}
          >
            <summary style={{ fontSize: 13, fontWeight: 700, color: '#333544', cursor: 'pointer' }}>
              Collection notice — how your data is collected and used
            </summary>
            <p style={{ fontSize: 13, lineHeight: 1.55, color: '#75757F', margin: '10px 0 0', whiteSpace: 'pre-wrap' }}>{noticeCopy}</p>
          </details>
          <label
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
              background: '#F9F9FB',
              border: '1px solid #E2E2E6',
              borderRadius: 10,
              padding: '14px 16px',
              cursor: 'pointer',
              marginBottom: 20,
            }}
          >
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsent(e.target.checked)}
              className="sr-only"
            />
            <span
              aria-hidden="true"
              style={{
                width: 20,
                height: 20,
                borderRadius: 5,
                background: consentChecked ? '#00D700' : '#fff',
                border: consentChecked ? '1px solid #00D700' : '1px solid #C8C8CC',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
                marginTop: 1,
              }}
            >
              {consentChecked && <Check size={13} strokeWidth={3} color="#000" />}
            </span>
            <span style={{ fontSize: 13.5, lineHeight: 1.5, color: '#333544' }}>
              I understand how my data is used and consent to taking the assessment.{' '}
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                style={{ color: '#00A800', fontWeight: 600, textDecoration: 'underline' }}
              >
                Read the full data-handling policy
              </a>
              .
            </span>
          </label>
          <PrimaryButton onClick={() => setAsmtStage('intro')} disabled={!consentChecked}>
            Agree &amp; continue {arrowIcon}
          </PrimaryButton>
        </div>
      </div>
    </StageBackdrop>
  );
}

function Intro() {
  const beginAssessment = useStore((s) => s.beginAssessment);
  // Prospective mode for the intro copy (the live session's authoritative mode is assigned
  // at create, after this screen). Conversational is the deeper 25–35 min interview.
  const conversational = useStore((s) => s.mode) === 'conversational';

  const rows: { icon: React.ReactNode; title: string; body: string; last?: boolean }[] = [
    {
      icon: <MessageSquare size={20} strokeWidth={1.7} color="#000" />,
      title: "What we'll do",
      body: 'Avery asks about a recent task, step by step, and listens for where the friction is.',
    },
    {
      icon: <Clock size={20} strokeWidth={1.7} color="#000" />,
      title: 'Why it matters',
      body: "Your real workflow is the evidence. It's how the report stays grounded in what you do — not a quiz.",
    },
    {
      icon: <ArrowRight size={20} strokeWidth={1.7} color="#000" />,
      title: 'No wrong answers',
      body: 'Skip, park or ask for an example any time. You can pause and come back.',
      last: true,
    },
  ];

  return (
    <StageBackdrop>
      <div
        className="animate-fadeUp"
        style={{
          maxWidth: 660,
          width: '100%',
          background: '#fff',
          border: '1px solid #E2E2E6',
          borderRadius: 14,
          boxShadow: '0 18px 48px rgba(15,17,26,0.10)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{ height: 5, background: 'linear-gradient(90deg,#00D700 0%,#00D700 33%,#EFEFF2 33%)' }}
        />
        <div style={{ padding: '40px 44px 44px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: '#00A800',
              }}
            >
              {conversational ? 'Three stages' : 'Stage 1 of 3'}
            </span>
            <span style={{ height: 14, width: 1, background: '#C8C8CC' }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#75757F' }}>
              {conversational ? 'About 25 to 35 minutes' : 'About 5 minutes'}
            </span>
          </div>
          <h1
            style={{
              fontSize: 34,
              lineHeight: 1.08,
              letterSpacing: '-0.025em',
              fontWeight: 700,
              margin: '0 0 6px',
              color: '#000',
            }}
          >
            {conversational ? 'Your AI fluency interview' : 'Workflow walkthrough & bottleneck finder'}
          </h1>
          <p
            style={{
              fontSize: 16,
              lineHeight: 1.55,
              color: '#75757F',
              margin: '14px 0 30px',
              maxWidth: '52ch',
            }}
          >
            A relaxed conversation about how you actually work — so we can find where your time really
            goes.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {rows.map((r) => (
              <div
                key={r.title}
                style={{
                  display: 'flex',
                  gap: 16,
                  padding: '16px 0',
                  borderTop: '1px solid #EFEFF2',
                  borderBottom: r.last ? '1px solid #EFEFF2' : undefined,
                }}
              >
                <span style={{ flex: 'none', marginTop: 2 }}>{r.icon}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#000', marginBottom: 3 }}>
                    {r.title}
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.5, color: '#75757F' }}>{r.body}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 30 }}>
            <PrimaryButton onClick={beginAssessment}>
              {conversational ? 'Begin the interview' : 'Begin stage 1'} {arrowIcon}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </StageBackdrop>
  );
}

const ROLE_OPTIONS = ['Sales / Presales', 'Engineering', 'Product / Delivery', 'Marketing', 'Customer Success', 'Operations', 'Leadership'];

// Asked only when the IdP didn't supply a role. The choice tailors the assessment (and tags
// the session). Picking a chip proceeds immediately; "Other" takes free text.
function RolePicker() {
  const setRole = useStore((s) => s.setRole);
  const beginSetup = useStore((s) => s.beginSetup);
  const [custom, setCustom] = useState('');

  const choose = (r: string) => {
    const role = r.trim();
    if (!role) return;
    setRole(role);
    beginSetup();
  };

  return (
    <StageBackdrop>
      <div
        className="animate-fadeUp"
        style={{ maxWidth: 620, width: '100%', background: '#fff', border: '1px solid #E2E2E6', borderRadius: 14, boxShadow: '0 18px 48px rgba(15,17,26,0.10)', overflow: 'hidden' }}
      >
        <div style={{ padding: '34px 40px 32px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#00A800', marginBottom: 16 }}>
            Tailoring to you
          </div>
          <h1 style={{ fontSize: 28, lineHeight: 1.1, letterSpacing: '-0.025em', fontWeight: 700, margin: 0, color: '#000' }}>
            What's your role?
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.55, color: '#75757F', margin: '12px 0 22px' }}>
            So Avery can make this about your actual work — not a generic quiz.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, marginBottom: 22 }}>
            {ROLE_OPTIONS.map((r) => (
              <button
                key={r}
                onClick={() => choose(r)}
                style={{ fontSize: 14, fontWeight: 600, color: '#000', background: '#F4F4F6', border: '1.5px solid #E2E2E6', borderRadius: 999, padding: '10px 16px', cursor: 'pointer', transition: 'all .12s' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#00D700'; e.currentTarget.style.background = '#F6FCF6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E2E2E6'; e.currentTarget.style.background = '#F4F4F6'; }}
              >
                {r}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
            <input
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') choose(custom); }}
              placeholder="Or type your role…"
              style={{ flex: 1, border: '1.5px solid #E2E2E6', borderRadius: 8, padding: '12px 14px', fontSize: 15, fontFamily: 'inherit', outline: 'none', color: '#000' }}
            />
            <PrimaryButton onClick={() => choose(custom)} disabled={!custom.trim()}>
              Continue {arrowIcon}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </StageBackdrop>
  );
}

function Setup() {
  return (
    <div
      className="animate-fade"
      style={{
        flex: 1,
        background: '#F4F4F6',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        minHeight: 'calc(100vh - 60px)',
      }}
    >
      <div style={{ position: 'relative', width: 84, height: 84, marginBottom: 30 }}>
        <span
          className="animate-pulseRing"
          style={{ position: 'absolute', inset: 0, borderRadius: 20, background: '#00D700', opacity: 0.4 }}
        />
        <span
          className="animate-pulseRing"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 20,
            background: '#00D700',
            opacity: 0.4,
            animationDelay: '0.7s',
          }}
        />
        <div
          className="animate-breathe"
          style={{
            position: 'relative',
            width: 84,
            height: 84,
            borderRadius: 20,
            background: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <VMark color="#00D700" size={42} />
        </div>
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#000', letterSpacing: '-0.01em' }}>
        Avery is setting up…
      </div>
      <div style={{ fontSize: 14, color: '#75757F', marginTop: 8 }}>
        Calibrating questions to your role. This only takes a moment.
      </div>
      <div style={{ display: 'flex', gap: 7, marginTop: 24 }}>
        {[0, 0.2, 0.4].map((d, i) => (
          <span
            key={i}
            className="animate-blink"
            style={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: '#00D700',
              animationDelay: `${d}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live assessment — interactive 6-step walkthrough (design handoff redesign).
// Avery hosts each step; the right rail builds the "Your profile" picture live.
// Committing a step applies skill gains and records a rendered answer; Finish
// submits the answers to Avery's scorer (store.nextStep) and opens the Report.
// ---------------------------------------------------------------------------

const vTile = (px: number, box: number) => (
  <div style={{ width: box, height: box, borderRadius: 9, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
    <VMark color="#00D700" size={px} />
  </div>
);

const Bolt = ({ size = 15 }: { size?: number }) => <Zap size={size} strokeWidth={2} color="#00D700" fill="#00D700" />;

/** The active step set — Avery's role-tailored steps when present, else the default. */
function useSteps() {
  return useStore((s) => s.tailoredSteps) ?? STEPS;
}

function Live() {
  const stepIdx = useStore((s) => s.stepIdx);
  const revealed = useStore((s) => s.revealed);
  const steps = useSteps();
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [stepIdx]);

  const step = steps[stepIdx];

  return (
    <div style={{ background: '#F4F4F6', minHeight: 'calc(100vh - 60px)' }}>
      <div
        className="fui-asmt-grid"
        style={{
          maxWidth: 1160,
          margin: '0 auto',
          background: '#fff',
          borderLeft: '1px solid #E2E2E6',
          borderRight: '1px solid #E2E2E6',
          minHeight: 'calc(100vh - 60px)',
          display: 'grid',
          gridTemplateColumns: '1fr 314px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <StepHeader stepIdx={stepIdx} revealed={revealed} />
          <div ref={scrollRef} className="fui-step-scroll" style={{ flex: 1, overflowY: 'auto', padding: '28px 30px 36px' }}>
            <div style={{ maxWidth: 640 }}>
              <AveryBubble text={step.avery} />
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#00A800', marginBottom: 12 }}>
                {step.eyebrow}
              </div>
              <StepBody step={step} />
            </div>
          </div>
        </div>
        <Rail />
      </div>
    </div>
  );
}

function StepHeader({ stepIdx, revealed }: { stepIdx: number; revealed: boolean }) {
  const steps = useSteps();
  const completed = stepIdx + (revealed ? 1 : 0);
  const pct = (completed / steps.length) * 100;
  return (
    <div style={{ flex: 'none', padding: '18px 30px 15px', borderBottom: '1px solid #EFEFF2' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          {vTile(18, 36)}
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: '#000' }}>Avery · your guide</div>
            <div style={{ fontSize: 12, color: '#75757F' }}>Stage 1 · Workflow &amp; leverage</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #E2E2E6', background: '#F9F9FB', borderRadius: 999, padding: '5px 11px', fontSize: 12, fontWeight: 600, color: '#333544' }}>
            <Clock size={13} strokeWidth={2} />
            {TIME_LEFT[stepIdx]}
          </span>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: '#00A800' }}>Topic {stepIdx + 1} of {steps.length}</span>
        </div>
      </div>
      <div style={{ marginTop: 13, height: 6, borderRadius: 999, background: '#EFEFF2', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: '#00D700', borderRadius: 999, transition: 'width .6s' }} />
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        {steps.map((_, i) => {
          const colour = i < stepIdx || (i === stepIdx && revealed) ? '#00D700' : i === stepIdx ? '#9FE89F' : '#E2E2E6';
          return <div key={i} style={{ flex: 1, height: 5, borderRadius: 999, background: colour, transition: 'all .4s' }} />;
        })}
      </div>
    </div>
  );
}

function AveryBubble({ text }: { text: string }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 20 }}>
      {vTile(17, 34)}
      <div style={{ background: '#F4F4F6', color: '#1A1A22', fontSize: 14.5, lineHeight: 1.55, padding: '13px 16px', borderRadius: 12, borderBottomLeftRadius: 3, maxWidth: '88%' }}>
        {text}
      </div>
    </div>
  );
}

// ---- Step bodies ----
function StepBody({ step }: { step: Step }) {
  switch (step.kind) {
    case 'scenario':
      return <ScenarioBody step={step} />;
    case 'prompt':
      return <PromptBody step={step} />;
    case 'rank':
      return <RankBody step={step} />;
    case 'slider':
      return <SliderBody step={step} />;
    case 'open':
      return <OpenBody step={step} />;
  }
}

type Scenario = Extract<Step, { kind: 'scenario' }>;
function ScenarioBody({ step }: { step: Scenario }) {
  const revealed = useStore((s) => s.revealed);
  const choice = useStore((s) => s.choice);
  const selectChoice = useStore((s) => s.selectChoice);
  return (
    <>
      <div style={{ border: '1px solid #E2E2E6', borderLeft: '3px solid #000', borderRadius: 10, background: '#FAFAFB', padding: '16px 18px', marginBottom: 18 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#75757F', marginBottom: 6 }}>The situation</div>
        <div style={{ fontSize: 15, lineHeight: 1.55, color: '#000' }}>{step.situation}</div>
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#000', marginBottom: 12 }}>{step.q}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {step.options.map((o) => {
          const picked = choice === o.id;
          const dimmed = revealed && !picked;
          return (
            <button
              key={o.id}
              onClick={() => selectChoice(o.id)}
              disabled={revealed}
              style={{
                display: 'flex',
                gap: 13,
                alignItems: 'flex-start',
                textAlign: 'left',
                borderRadius: 9,
                padding: '14px 16px',
                border: `1.5px solid ${picked ? '#00D700' : '#E2E2E6'}`,
                background: picked ? '#F6FCF6' : '#fff',
                boxShadow: picked ? '0 0 0 3px rgba(0,215,0,0.12)' : 'none',
                opacity: dimmed ? 0.55 : 1,
                cursor: revealed ? 'default' : 'pointer',
                transition: 'all .14s',
                fontFamily: 'inherit',
              }}
            >
              <span style={{ width: 22, height: 22, borderRadius: '50%', border: `1.5px solid ${picked ? '#00D700' : '#C8C8CC'}`, background: picked ? '#00D700' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', marginTop: 1 }}>
                {picked && <Check size={12} strokeWidth={3} color="#000" />}
              </span>
              <span>
                <span style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: '#000' }}>{o.label}</span>
                <span style={{ display: 'block', fontSize: 12.5, color: '#75757F', marginTop: 2 }}>{o.sub}</span>
              </span>
            </button>
          );
        })}
      </div>
      <AfterStep step={step} />
    </>
  );
}

type Prompt = Extract<Step, { kind: 'prompt' }>;
function PromptBody({ step }: { step: Prompt }) {
  const revealed = useStore((s) => s.revealed);
  const promptSel = useStore((s) => s.promptSel);
  const toggleClause = useStore((s) => s.toggleClause);
  const selected = step.clauses.filter((c) => promptSel[c.key]);
  const assembled = [step.base, ...selected.map((c) => c.clause)].join(' ');
  const q = promptQuality(selected.length);
  return (
    <>
      <div style={{ background: '#0B0B0F', borderRadius: 10, padding: '16px 18px', marginBottom: 16 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: '#00D700', marginBottom: 8, letterSpacing: '0.08em' }}>Your prompt</div>
        <div style={{ fontSize: 14.5, lineHeight: 1.6, color: '#E8E8EC' }}>{assembled}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1, height: 8, borderRadius: 999, background: '#EFEFF2', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${q.pct}%`, background: '#00D700', borderRadius: 999, transition: 'width .4s' }} />
        </div>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: '#00A800', whiteSpace: 'nowrap' }}>{q.label}</span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#333544', marginBottom: 10 }}>Tap what you'd add to sharpen it</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {step.clauses.map((c) => {
          const on = !!promptSel[c.key];
          return (
            <Chip key={c.key} on={on} disabled={revealed} onClick={() => toggleClause(c.key)} label={c.label} />
          );
        })}
      </div>
      <AfterStep step={step} />
    </>
  );
}

type Rank = Extract<Step, { kind: 'rank' }>;
function RankBody({ step }: { step: Rank }) {
  const revealed = useStore((s) => s.revealed);
  const rankOrder = useStore((s) => s.rankOrder);
  const moveRank = useStore((s) => s.moveRank);
  const order = rankOrder ?? step.items;
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {order.map((item, i) => (
          <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1.5px solid #E2E2E6', borderRadius: 8, padding: '10px 12px' }}>
            <span style={{ width: 24, height: 24, borderRadius: 6, background: '#00D700', color: '#000', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>{i + 1}</span>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: '#000' }}>{item}</span>
            <RankArrow dir={-1} disabled={revealed || i === 0} onClick={() => moveRank(i, -1)} />
            <RankArrow dir={1} disabled={revealed || i === order.length - 1} onClick={() => moveRank(i, 1)} />
          </div>
        ))}
      </div>
      <div style={{ fontSize: 12.5, color: '#75757F', marginTop: 10 }}>Top = where AI should take the most off your plate.</div>
      <AfterStep step={step} />
    </>
  );
}

function RankArrow({ dir, disabled, onClick }: { dir: -1 | 1; disabled: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={dir === -1 ? 'Move up' : 'Move down'}
      style={{ width: 30, height: 30, borderRadius: 6, border: '1px solid #E2E2E6', background: '#fff', color: disabled ? '#D2D2D6' : '#333544', cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}
    >
      {dir === -1 ? <ChevronUp size={16} strokeWidth={2.2} /> : <ChevronDown size={16} strokeWidth={2.2} />}
    </button>
  );
}

type Slider = Extract<Step, { kind: 'slider' }>;
function SliderBody({ step }: { step: Slider }) {
  const revealed = useStore((s) => s.revealed);
  const sliderVal = useStore((s) => s.sliderVal);
  const setSlider = useStore((s) => s.setSlider);
  const zone = sliderZone(sliderVal);
  return (
    <>
      <div style={{ border: '1px solid #E2E2E6', borderRadius: 12, background: '#FAFAFB', padding: '22px 22px 18px' }}>
        <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', color: '#000' }}>{zone.label}</div>
        <div style={{ fontSize: 13.5, color: '#75757F', marginTop: 4 }}>{zone.reaction}</div>
        <input
          type="range"
          min={0}
          max={100}
          value={sliderVal}
          disabled={revealed}
          onChange={(e) => setSlider(Number(e.target.value))}
          style={{ width: '100%', accentColor: '#00D700', height: 6, marginTop: 18 }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, fontWeight: 600, color: '#75757F', marginTop: 6 }}>
          <span>Rewrite it all</span>
          <span>Edit framing &amp; facts</span>
          <span>Light touch</span>
        </div>
      </div>
      <AfterStep step={step} />
    </>
  );
}

type Open = Extract<Step, { kind: 'open' }>;
function OpenBody({ step }: { step: Open }) {
  const revealed = useStore((s) => s.revealed);
  const openText = useStore((s) => s.openText);
  const setOpenText = useStore((s) => s.setOpenText);
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        {step.chips.map((c) => (
          <Chip key={c} on={openText === c} disabled={revealed} onClick={() => setOpenText(c)} label={c} />
        ))}
      </div>
      <textarea
        value={openText}
        disabled={revealed}
        onChange={(e) => setOpenText(e.target.value)}
        rows={3}
        placeholder="Or say it your own way…"
        style={{ width: '100%', border: '1.5px solid #E2E2E6', borderRadius: 12, padding: '13px 15px', fontSize: 15, lineHeight: 1.5, background: '#fff', fontFamily: 'inherit', resize: 'vertical', outline: 'none' }}
      />
      <AfterStep step={step} />
    </>
  );
}

function Chip({ on, disabled, onClick, label }: { on: boolean; disabled: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        borderRadius: 999,
        padding: '8px 13px',
        fontSize: 13,
        fontWeight: 600,
        border: `1.5px solid ${on ? '#00D700' : '#E2E2E6'}`,
        background: on ? '#00D700' : '#fff',
        color: '#000',
        cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'inherit',
        transition: 'all .12s',
      }}
    >
      {on ? <Check size={13} strokeWidth={3} /> : <Plus size={13} strokeWidth={2.4} />}
      {label}
    </button>
  );
}

// ---- Reveal + action row (shared) ----
function AfterStep({ step }: { step: Step }) {
  const revealed = useStore((s) => s.revealed);
  const working = useStore((s) => ({ choice: s.choice, promptSel: s.promptSel, rankOrder: s.rankOrder, sliderVal: s.sliderVal, openText: s.openText }));
  const lockIn = useStore((s) => s.lockIn);
  const nextStep = useStore((s) => s.nextStep);
  const stepIdx = useStore((s) => s.stepIdx);
  const steps = useSteps();

  if (revealed) {
    const isLast = stepIdx === steps.length - 1;
    return (
      <>
        <RevealPanel step={step} text={revealText(step, working)} />
        <button
          onClick={nextStep}
          style={{ marginTop: 18, width: '100%', padding: 14, borderRadius: 6, fontSize: 15, fontWeight: 700, background: '#00D700', color: '#000', border: '2px solid #00D700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9, transition: 'all .12s' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#00BE00'; e.currentTarget.style.borderColor = '#00BE00'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#00D700'; e.currentTarget.style.borderColor = '#00D700'; }}
        >
          {isLast ? 'Finish & see your report' : 'Next topic'}
          <ArrowRight size={17} strokeWidth={2.2} />
        </button>
      </>
    );
  }

  // scenario commits on card click — no lock button.
  if (step.kind === 'scenario') return null;

  const label = step.kind === 'prompt' ? 'Lock in my prompt' : step.kind === 'rank' ? 'Lock in my order' : step.kind === 'slider' ? 'Lock it in' : 'Share with Avery';
  const disabled = step.kind === 'open' && working.openText.trim().length === 0;
  return (
    <button
      onClick={lockIn}
      disabled={disabled}
      style={{
        marginTop: 20,
        width: '100%',
        padding: 14,
        borderRadius: 6,
        fontSize: 15,
        fontWeight: 700,
        background: disabled ? '#EEEEF1' : '#000',
        color: disabled ? '#B4B4BA' : '#fff',
        border: `2px solid ${disabled ? '#EEEEF1' : '#000'}`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 9,
        transition: 'all .12s',
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = '#1A1A22'; }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.background = '#000'; }}
    >
      {label}
      <Check size={17} strokeWidth={2.4} />
    </button>
  );
}

function RevealPanel({ step, text }: { step: Step; text: string }) {
  return (
    <div className="animate-fadeUp" style={{ marginTop: 22, border: '1px solid #C8E6C8', borderLeft: '3px solid #00D700', borderRadius: 10, background: '#F6FCF6', padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 9 }}>
        <Bolt size={15} />
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#00A800' }}>Insight unlocked</span>
      </div>
      <div style={{ fontSize: 14.5, lineHeight: 1.55, color: '#000' }}>{text}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid #DBEEDB' }}>
        {gainChips(step).map((g) => (
          <span key={g.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: '#00A800', background: '#fff', border: '1px solid #C8E6C8', borderRadius: 999, padding: '4px 10px' }}>
            <ArrowUp size={11} strokeWidth={2.6} />
            {g.label} +{g.delta}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---- Right rail: "Your profile" ----
function Rail() {
  const skills = useStore((s) => s.skills);
  const insightsCount = useStore((s) => s.insightsCount);
  const viewSample = useStore((s) => s.viewSample);
  const total = useSteps().length;

  const momentum =
    insightsCount === 0
      ? 'Answer the first topic to start building your profile.'
      : insightsCount >= total
        ? 'All topics done — your report is ready.'
        : 'Each answer sharpens your report. Keep the streak going.';

  return (
    <div className="fui-rail" style={{ borderLeft: '1px solid #EFEFF2', background: '#FAFAFB', padding: '24px 22px', display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#000' }}>Your profile</div>
        <div style={{ fontSize: 12.5, color: '#75757F', marginTop: 4 }}>Building live as you answer — this becomes your report.</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        {SKILL_KEYS.map((k) => {
          const v = skills[k];
          const band = skillBand(v);
          return (
            <div key={k}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: '#000' }}>{SKILL_LABELS[k]}</span>
                  <InfoTip label={SKILL_LABELS[k]} text={SKILL_DEF[k]} />
                </span>
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '2px 7px', borderRadius: 4, background: band.bg, color: band.ink }}>{band.label}</span>
              </div>
              <div style={{ height: 7, borderRadius: 999, background: '#EAEAEE', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(v / 5) * 100}%`, background: '#00D700', borderRadius: 999, transition: 'width .8s' }} />
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ background: '#000', borderRadius: 10, padding: '15px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Bolt size={18} />
          <span style={{ fontSize: 22, fontWeight: 800, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>{insightsCount}</span>
          <span style={{ fontSize: 11.5, color: '#9A9AA4' }}>insights captured</span>
        </div>
        <div style={{ fontSize: 12, color: '#ADAEB4', marginTop: 8 }}>{momentum}</div>
      </div>
      <button
        onClick={viewSample}
        style={{ marginTop: 'auto', fontSize: 12.5, fontWeight: 700, color: '#75757F', background: 'transparent', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0 }}
        onMouseEnter={(e) => (e.currentTarget.style.color = '#000')}
        onMouseLeave={(e) => (e.currentTarget.style.color = '#75757F')}
      >
        Skip to a sample report
        <ArrowRight size={14} strokeWidth={2.2} />
      </button>
    </div>
  );
}
